/**
 * Core logger implementation
 * Main logging class that combines all logging subsystems
 */

import { getLogConfig, isDomainEnabled, isLevelEnabled } from './config';
import { ContextManager } from './context';
import { DebugSessionManagerImpl } from './debug-session';
import { formatLogEntry, formatPhaseTransition } from './formatters';
import type {
  DomainLogger,
  LevelLogger,
  LogContext,
  LogEntry,
  LogLevel,
  PhaseManager,
} from './types';

export class LoggerImpl {
  private contextManager = new ContextManager();
  private debugSessionManager = new DebugSessionManagerImpl();
  private currentPhase: string | null = null;
  private config = getLogConfig();

  // Level-based loggers
  public L1: LevelLogger;
  public L2: LevelLogger;
  public L3: LevelLogger;
  public L4: LevelLogger;
  public L5: LevelLogger;

  // Domain logger
  public domain: DomainLogger;

  // Phase management
  public phase: PhaseManager;

  // Debug session management
  public debug = {
    start: (sessionId: string) => this.debugSessionManager.start(sessionId),
    end: (sessionId: string) => this.debugSessionManager.end(sessionId),
    trace: (sessionId: string, message: string) =>
      this.debugSessionManager.trace(sessionId, message),
    isActive: () => this.debugSessionManager.isActive(),
    getActiveSessions: () => this.debugSessionManager.getActiveSessions(),
  };

  constructor() {
    // Initialize level loggers
    this.L1 = this.createLevelLogger('L1', 'Project');
    this.L2 = this.createLevelLogger('L2', 'Configuration');
    this.L3 = this.createLevelLogger('L3', 'Managers');
    this.L4 = this.createLevelLogger('L4', 'Engines');
    this.L5 = this.createLevelLogger('L5', 'Calculators');

    // Initialize domain logger
    this.domain = this.createDomainLogger();

    // Initialize phase manager
    this.phase = this.createPhaseManager();
  }

  /**
   * Create a level-specific logger
   */
  private createLevelLogger(
    level: keyof typeof this.config.enabledLevels,
    prefix: string
  ): LevelLogger {
    const log = (logLevel: LogLevel, message: string, context?: LogContext) => {
      if (!isLevelEnabled(level, this.config)) return;

      const entry: LogEntry = {
        timestamp: new Date().toISOString(),
        level: logLevel,
        message: `[${prefix}] ${message}`,
        context: { ...this.contextManager.getCurrentContext(), ...context },
        phase: this.currentPhase || undefined,
      };

      this.outputLog(entry);
    };

    return {
      error: (message: string, context?: LogContext) => {
        // Errors always log regardless of level
        const entry: LogEntry = {
          timestamp: new Date().toISOString(),
          level: 'error',
          message: `[${prefix}] ${message}`,
          context: { ...this.contextManager.getCurrentContext(), ...context },
          phase: this.currentPhase || undefined,
        };
        console.error(formatLogEntry(entry, this.config));
      },
      warn: (message: string, context?: LogContext) => log('warn', message, context),
      info: (message: string, context?: LogContext) => log('info', message, context),
      debug: (message: string, context?: LogContext) => log('debug', message, context),
      trace: (message: string, context?: LogContext) => log('trace', message, context),
    };
  }

  /**
   * Create the domain logger
   */
  private createDomainLogger(): DomainLogger {
    const logDomain = (
      domain: keyof typeof this.config.enabledDomains,
      message: string,
      context?: LogContext
    ) => {
      if (!isDomainEnabled(domain, this.config)) return;

      const entry: LogEntry = {
        timestamp: new Date().toISOString(),
        level: 'info',
        message,
        context: { ...this.contextManager.getCurrentContext(), ...context },
        phase: this.currentPhase || undefined,
        domain,
      };

      this.outputLog(entry);
    };

    return {
      compliance: (message: string, context?: LogContext) =>
        logDomain('compliance', message, context),
      optimization: (message: string, context?: LogContext) =>
        logDomain('optimization', message, context),
      distribution: (message: string, context?: LogContext) =>
        logDomain('distribution', message, context),
      interest: (message: string, context?: LogContext) => logDomain('interest', message, context),
      validation: (message: string, context?: LogContext) =>
        logDomain('validation', message, context),
      performance: (message: string, context?: LogContext) =>
        logDomain('performance', message, context),
    };
  }

  /**
   * Create the phase manager
   */
  private createPhaseManager(): PhaseManager {
    return {
      enter: (phaseName: string, metadata?: LogContext) => {
        this.currentPhase = phaseName;
        if (this.config.showContext) {
          console.log(formatPhaseTransition('enter', phaseName, metadata));
        }
        // Add phase to context
        this.contextManager.addGlobalContext({ phase: phaseName, ...metadata });
      },
      exit: (phaseName: string, result?: LogContext) => {
        if (this.config.showContext) {
          console.log(formatPhaseTransition('exit', phaseName, result));
        }
        // Clear phase from context
        this.contextManager.clearGlobalContext('phase');
        this.currentPhase = null;
      },
      current: () => this.currentPhase,
    };
  }

  /**
   * Output a log entry to console
   */
  private outputLog(entry: LogEntry): void {
    const formatted = formatLogEntry(entry, this.config);

    switch (entry.level) {
      case 'error':
        console.error(formatted);
        break;
      case 'warn':
        console.warn(formatted);
        break;
      case 'trace':
      case 'debug':
        console.debug(formatted);
        break;
      default:
        console.log(formatted);
    }
  }

  /**
   * Add context that will be included in all subsequent logs
   */
  public withContext(context: LogContext): this {
    this.contextManager.addGlobalContext(context);
    return this;
  }

  /**
   * Execute a function with additional scoped context
   */
  public scoped<T>(context: LogContext, fn: () => T): T {
    return this.contextManager.withContext(context, fn);
  }

  /**
   * Clear global context
   */
  public clearContext(...keys: string[]): void {
    this.contextManager.clearGlobalContext(...keys);
  }

  /**
   * General error logging (always enabled)
   */
  public error(message: string, error?: Error | unknown): void {
    const errorInfo: LogContext = {};

    if (error instanceof Error) {
      errorInfo.errorMessage = error.message;
      errorInfo.errorStack = error.stack;
    } else if (error) {
      errorInfo.error = String(error);
    }

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level: 'error',
      message,
      context: { ...this.contextManager.getCurrentContext(), ...errorInfo },
      phase: this.currentPhase || undefined,
    };

    console.error(formatLogEntry(entry, this.config));
  }

  /**
   * Reload configuration (useful for runtime changes)
   */
  public reloadConfig(): void {
    this.config = getLogConfig();
  }
}
